import contactGroupExampleImage from '../../../assets/images/img_contact_group_example.jpg';

export default class ContactGroupsController {
    /* @ngInject */
    constructor($scope, $sce, IAMService, ToastService, DialogService) {
        this.$scope = $scope;
        this.$sce = $sce;
        this.IAMService = IAMService;
        this.ToastService = ToastService;
        this.DialogService = DialogService;
        
        // Initialize state
        this.newContactGroupName = '';
        this.newContactGroupDescription = '';
        this.editingContactGroup = null;
        this.editingContact = null;
        
        // Add Contact state
        this.addingContact = null; // Format: {contactGroupName: string, projectName: string}
        this.newContact = {
            displayName: '',
            email: '',
            phoneNumber: ''
        };
        
        // Expansion state tracking
        this.expandedContactGroups = new Set();
        this.expandedProjects = new Set();
        this.contactGroupContacts = new Map(); // contactGroupName -> {projects: Map, loading: boolean}
        
        // Search state
        this.projectSearchTerms = new Map(); // contactGroupName -> searchTerm
        
        // Organization projects
        this.organizationProjects = [];
        this.organizationProjectsLoaded = false;
        this.loadingOrganizationProjects = false;
        
        // Images
        this.contactGroupExampleImage = contactGroupExampleImage;
    }

    $onInit() {
        this.loadOrganizationProjects();
    }

    async loadOrganizationProjects() {
        if (this.organizationProjectsLoaded || this.loadingOrganizationProjects) {
            return;
        }

        this.loadingOrganizationProjects = true;
        
        // Get organization name from organizationId (format: organizations/org-name)
        const organizationName = this.organizationId.includes('/') ? 
            this.organizationId : 
            `organizations/${this.organizationId}`;
        
        try {
            const allProjects = await this.fetchAllProjectsForOrganization(organizationName);
            this.organizationProjects = allProjects || [];
            this.organizationProjectsLoaded = true;
        } catch (error) {
            this.organizationProjects = [];
        } finally {
            this.loadingOrganizationProjects = false;
            this.$scope.$applyAsync();
        }
    }

    async fetchAllProjectsForOrganization(organizationName) {
        return this.getProjectsPage(organizationName, null, []);
    }

    getProjectsPage(organizationName, pageToken, previousData) {
        const requestParams = {
            organization: organizationName,
            pageToken
        };
        return this.IAMService
            .projects(requestParams)
            .then(({ data, nextPageToken }) => {
                const allData = [...previousData, ...data];
                if (nextPageToken !== '') {
                    return this.getProjectsPage(organizationName, nextPageToken, allData);
                }
                return allData;
            });
    }

    // Contact Group Expansion Methods
    toggleContactGroup(contactGroup) {
        const groupName = contactGroup.name;
        
        if (this.expandedContactGroups.has(groupName)) {
            // Collapse
            this.expandedContactGroups.delete(groupName);
            // Also collapse all projects within this group
            if (this.contactGroupContacts.has(groupName)) {
                const groupData = this.contactGroupContacts.get(groupName);
                Array.from(groupData.projects.keys()).forEach(projectName => {
                    this.expandedProjects.delete(`${groupName}:${projectName}`);
                });
            }
        } else {
            // Expand
            this.expandedContactGroups.add(groupName);
            // Load organization projects when first contact group is expanded
            this.loadOrganizationProjects();
            this.loadContactsForGroup(contactGroup);
        }
        
        // Force update to ensure UI reflects the state change
        this.$scope.$applyAsync();
    }

    toggleProject(contactGroupName, projectName) {
        const projectKey = `${contactGroupName}:${projectName}`;
        
        if (this.expandedProjects.has(projectKey)) {
            this.expandedProjects.delete(projectKey);
        } else {
            this.expandedProjects.add(projectKey);
        }
        
        // Force update to ensure UI reflects the state change
        this.$scope.$applyAsync();
    }

    isContactGroupExpanded(contactGroupName) {
        return this.expandedContactGroups.has(contactGroupName);
    }

    isProjectExpanded(contactGroupName, projectName) {
        return this.expandedProjects.has(`${contactGroupName}:${projectName}`);
    }

    async loadContactsForGroup(contactGroup) {
        const groupName = contactGroup.name;
        
        // Check if we already have data for this group
        if (this.contactGroupContacts.has(groupName)) {
            // Already have data, just ensure UI is updated
            this.$scope.$applyAsync();
            return;
        }
        
        // Set loading state
        this.contactGroupContacts.set(groupName, {
            projects: new Map(),
            loading: true
        });
        this.$scope.$applyAsync();

        try {
            // Get organization name from organizationId (format: organizations/org-name)
            const organizationName = this.organizationId.includes('/') ? 
                this.organizationId : 
                `organizations/${this.organizationId}`;
            
            const params = {
                organization: organizationName,
                contactGroup: groupName
            };
            
            const response = await this.IAMService.listContactsForOrganization(params);
            
            // Group contacts by project
            const projectsMap = new Map();
            const contacts = response.contacts || [];
            
            contacts.forEach(contact => {
                const projectId = this.extractProjectIdFromContactName(contact.name);
                const projectName = `projects/${projectId}`;
                
                if (!projectsMap.has(projectName)) {
                    projectsMap.set(projectName, []);
                }
                projectsMap.get(projectName).push(contact);
            });

            // Update the contacts data
            this.contactGroupContacts.set(groupName, {
                projects: projectsMap,
                loading: false,
                cachedProjects: null,
                lastCacheKey: null
            });

        } catch (error) {
            // Set empty data on error
            this.contactGroupContacts.set(groupName, {
                projects: new Map(),
                loading: false,
                cachedProjects: null,
                lastCacheKey: null
            });
        }
        
        this.$scope.$applyAsync();
    }

    extractProjectIdFromContactName(contactName) {
        // Contact name format: "projects/{project-id}/contacts/{contact-id}"
        const parts = contactName.split('/');
        return parts[1]; // The project ID (not parts[3] which is the contact ID)
    }

    getContactsDataForGroup(contactGroupName) {
        return this.contactGroupContacts.get(contactGroupName) || { projects: new Map(), loading: false };
    }

    getProjectsForGroup(contactGroupName) {
        const groupData = this.getContactsDataForGroup(contactGroupName);
        const searchTerm = this.projectSearchTerms.get(contactGroupName) || '';
        
        // Cache the projects array to prevent recreating it on every digest cycle
        const orgProjectsCount = this.organizationProjects.length;
        const cacheKey = `${orgProjectsCount}_${groupData.projects.size}_${searchTerm}_${this.organizationProjectsLoaded}`;
        if (!groupData.cachedProjects || groupData.lastCacheKey !== cacheKey) {
            let allProjects = [];
            
            // Use all organization projects if loaded, otherwise fall back to contact-based projects
            if (this.organizationProjectsLoaded && this.organizationProjects.length > 0) {
                allProjects = this.organizationProjects.map(orgProject => {
                    const contacts = groupData.projects.get(orgProject.name) || [];
                    return {
                        name: orgProject.name,
                        displayName: orgProject.displayName || this.getProjectDisplayName(orgProject.name),
                        contacts: contacts,
                        contactCount: contacts.length
                    };
                });
            } else {
                // Fallback to old behavior if organization projects aren't loaded yet
                allProjects = Array.from(groupData.projects.entries()).map(([projectName, contacts]) => ({
                    name: projectName,
                    displayName: this.getProjectDisplayName(projectName),
                    contacts: contacts,
                    contactCount: contacts.length
                }));
            }
            
            // Filter by search term if present
            if (searchTerm) {
                allProjects = allProjects.filter(project => 
                    project.displayName.toLowerCase().includes(searchTerm.toLowerCase())
                );
            }
            
            // Sort by contact count (descending) then by display name (ascending)
            allProjects.sort((a, b) => {
                // First sort by contact count (descending - higher counts first)
                const contactCountDiff = b.contactCount - a.contactCount;
                if (contactCountDiff !== 0) {
                    return contactCountDiff;
                }
                // If contact counts are equal, sort alphabetically by display name
                return a.displayName.localeCompare(b.displayName);
            });
            
            // Cache the result
            groupData.cachedProjects = allProjects;
            groupData.lastCacheKey = cacheKey;
        }
        
        return groupData.cachedProjects || [];
    }

    getProjectDisplayName(projectName) {
        // Project name format: "projects/{project-id}"
        const projectId = projectName.split('/')[1];
        
        // Try to find the project in organization projects first
        if (this.organizationProjects.length > 0) {
            const orgProject = this.organizationProjects.find(p => p.name === projectName);
            if (orgProject && orgProject.displayName) {
                return orgProject.displayName;
            }
        }
        
        // Fallback to project ID
        return projectId;
    }

    getTotalContactsInGroup(contactGroupName) {
        const groupData = this.getContactsDataForGroup(contactGroupName);
        return Array.from(groupData.projects.values()).reduce((total, contacts) => total + contacts.length, 0);
    }

    // Method to get the actual contact count for display
    getDisplayContactCount(contactGroup) {
        const groupName = contactGroup.name;
        if (this.contactGroupContacts.has(groupName)) {
            const groupData = this.contactGroupContacts.get(groupName);
            // If still loading, use the original contact count to avoid showing 0
            if (groupData.loading) {
                return contactGroup.contactCount || 0;
            }
            return this.getTotalContactsInGroup(groupName);
        }
        return contactGroup.contactCount || 0;
    }

    async createContactGroup() {
        if (!this.newContactGroupName.trim()) {
            return;
        }

        try {
            await this.onCreateContactGroup({
                displayName: this.newContactGroupName.trim(),
                description: this.newContactGroupDescription.trim()
            });
            
            this.newContactGroupName = '';
            this.newContactGroupDescription = '';
            this.ToastService.showSimpleText('Contact group created successfully');
        } catch (error) {
            this.ToastService.showSimpleText(`Failed to create contact group: ${error.data?.error || error.message || 'Unknown error'}`);
        }
        this.$scope.$applyAsync();
    }

    startEditingContactGroup(contactGroup) {
        this.editingContactGroup = {
            ...contactGroup,
            newDisplayName: contactGroup.displayName,
            newDescription: contactGroup.description
        };
    }

    cancelEditingContactGroup() {
        this.editingContactGroup = null;
    }

    async saveContactGroup() {
        if (!this.editingContactGroup.newDisplayName.trim()) {
            this.ToastService.showSimpleText('Please enter a contact group name');
            return;
        }

        try {
            await this.onUpdateContactGroup({
                contactGroup: this.editingContactGroup,
                displayName: this.editingContactGroup.newDisplayName.trim(),
                description: this.editingContactGroup.newDescription.trim()
            });
            
            this.editingContactGroup = null;
            this.ToastService.showSimpleText('Contact group updated successfully');
        } catch (error) {
            this.ToastService.showSimpleText(`Failed to update contact group: ${error.data?.error || error.message || 'Unknown error'}`);
        }
        this.$scope.$applyAsync();
    }

    confirmDeleteContactGroup(contactGroup) {
        const confirm = this.DialogService.confirm({
            title: 'Delete Contact Group',
            textContent: `Are you sure you want to delete "${contactGroup.displayName}"? This action cannot be undone.`,
            ok: 'Delete',
            cancel: 'Cancel'
        });
        confirm.then(() => {
            this.onDeleteContactGroup({ contactGroup: contactGroup });
        });
    }

    // Contact Management Methods
    startEditingContact(contact) {
        this.editingContact = {
            ...contact,
            newDisplayName: contact.displayName,
            newEmail: contact.email,
            newPhone: contact.phoneNumber
        };
    }

    cancelEditingContact() {
        this.editingContact = null;
    }

    async saveContact() {
        if (!this.editingContact.newDisplayName.trim()) {
            this.ToastService.showSimpleText('Please enter a contact name');
            return;
        }

        try {
            const contactId = this.editingContact.name.split('/').pop();
            const projectId = this.extractProjectIdFromContactName(this.editingContact.name);

            const updatedContactData = {
                displayName: this.editingContact.newDisplayName.trim(),
                email: this.editingContact.newEmail.trim() || null,
                phoneNumber: this.editingContact.newPhone.trim() || null
            };

            const updatedContact = await this.IAMService.updateContact(projectId, contactId, updatedContactData);
            
            // Update the contact in our local data structure
            this.updateContactInData(this.editingContact.name, updatedContact);
            
            this.editingContact = null;
            this.ToastService.showSimpleText('Contact updated successfully');
        } catch (error) {
            this.ToastService.showSimpleText(`Failed to update contact: ${error.data?.error || error.message || 'Unknown error'}`);
        }
        this.$scope.$applyAsync();
    }

    confirmDeleteContact(contact) {
        const confirm = this.DialogService.confirm({
            title: 'Delete Contact',
            textContent: `Are you sure you want to delete "${contact.displayName}"? This action cannot be undone.`,
            ok: 'Delete',
            cancel: 'Cancel'
        });
        confirm.then(() => {
            this.deleteContact(contact);
        });
    }

    async deleteContact(contact) {
        try {
            const contactId = contact.name.split('/').pop();
            const projectId = this.extractProjectIdFromContactName(contact.name);
            
            await this.IAMService.deleteContact(projectId, contactId);
            
            // Remove from local data structure
            this.removeContactFromData(contact.name);
            
            this.ToastService.showSimpleText('Contact deleted successfully');
        } catch (error) {
            this.ToastService.showSimpleText(`Failed to delete contact: ${error.data?.error || error.message || 'Unknown error'}`);
        }
        
        // Refresh contact groups data
        await this.onLoadContactGroups();
        this.$scope.$applyAsync();
    }

    findContactInData(contactName) {
        const contactGroupEntries = Array.from(this.contactGroupContacts.entries());
        return contactGroupEntries.reduce((foundContact, [, groupData]) => {
            if (foundContact) return foundContact;
            
            const projectEntries = Array.from(groupData.projects.entries());
            return projectEntries.reduce((found, [, contacts]) => {
                if (found) return found;
                return contacts.find(c => c.name === contactName) || null;
            }, null);
        }, null);
    }

    removeContactFromData(contactName) {
        const contactGroupEntries = Array.from(this.contactGroupContacts.entries());
        contactGroupEntries.some(([, groupData]) => {
            const projectEntries = Array.from(groupData.projects.entries());
            return projectEntries.some(([, contacts]) => {
                const contactIndex = contacts.findIndex(c => c.name === contactName);
                if (contactIndex !== -1) {
                    contacts.splice(contactIndex, 1);
                    return true;
                }
                return false;
            });
        });
    }

    updateContactInData(contactName, updatedContact) {
        // Find and update the contact in the data structure
        const contactGroupEntries = Array.from(this.contactGroupContacts.entries());
        contactGroupEntries.some(([, groupData]) => {
            const projectEntries = Array.from(groupData.projects.entries());
            return projectEntries.some(([, contacts]) => {
                const contactIndex = contacts.findIndex(c => c.name === contactName);
                if (contactIndex !== -1) {
                    contacts[contactIndex] = { ...contacts[contactIndex], ...updatedContact };
                    // Clear cached projects to force refresh
                    groupData.cachedProjects = null;
                    groupData.lastCacheKey = null;
                    return true;
                }
                return false;
            });
        });
    }

    clearCachedProjectsForAllGroups() {
        const contactGroupEntries = Array.from(this.contactGroupContacts.entries());
        contactGroupEntries.forEach(([, groupData]) => {
            groupData.cachedProjects = null;
            groupData.lastCacheKey = null;
        });
    }

    clearAllContactData() {
        this.contactGroupContacts.clear();
        this.expandedContactGroups.clear();
        this.expandedProjects.clear();
    }

    isAddingContact(contactGroupName, projectName) {
        return this.addingContact && 
               this.addingContact.contactGroupName === contactGroupName && 
               this.addingContact.projectName === projectName;
    }

    startAddingContact(contactGroupName, project) {
        this.addingContact = {
            contactGroupName: contactGroupName,
            projectName: project.name
        };
        
        // Clear the form
        this.newContact = {
            displayName: '',
            email: '',
            phoneNumber: ''
        };
    }

    cancelAddingContact() {
        this.addingContact = null;
        this.newContact = {
            displayName: '',
            email: '',
            phoneNumber: ''
        };
    }

    async addContact() {
        if (!this.newContact.displayName.trim()) {
            this.ToastService.showSimpleText('Please enter a contact name');
            return;
        }

        if (!this.newContact.email.trim() && !this.newContact.phoneNumber.trim()) {
            this.ToastService.showSimpleText('Please provide either an email or phone number');
            return;
        }

        try {
            // Find the contact group
            const contactGroup = this.contactGroups.find(cg => cg.name === this.addingContact.contactGroupName);
            if (!contactGroup) {
                this.ToastService.showSimpleText('Contact group not found');
                return;
            }

            // Extract project ID from project name
            const projectId = this.addingContact.projectName.split('/')[1];

            const contactData = {
                displayName: this.newContact.displayName.trim(),
                email: this.newContact.email.trim() || null,
                phoneNumber: this.newContact.phoneNumber.trim() || null,
                contactGroup: contactGroup.name
            };

            const createdContact = await this.IAMService.createContact(projectId, contactData);
            
            // Add the new contact directly to the existing data structure (no full reload)
            const groupData = this.contactGroupContacts.get(this.addingContact.contactGroupName);
            if (groupData && groupData.projects.has(this.addingContact.projectName)) {
                // Add to existing project contacts
                groupData.projects.get(this.addingContact.projectName).push(createdContact);
            } else if (groupData) {
                // Create new project entry if it doesn't exist
                groupData.projects.set(this.addingContact.projectName, [createdContact]);
            }
            
            // Clear only the cached projects for this specific contact group to refresh counts
            if (groupData) {
                groupData.cachedProjects = null;
                groupData.lastCacheKey = null;
            }

            this.ToastService.showSimpleText('Contact added successfully');
            this.cancelAddingContact();
            
        } catch (error) {
            this.ToastService.showSimpleText(`Failed to add contact: ${error.data?.error || error.message || 'Unknown error'}`);
        }
        this.$scope.$applyAsync();
    }

    // Search Methods
    updateProjectSearch(contactGroupName, searchTerm) {
        this.projectSearchTerms.set(contactGroupName, searchTerm);
        
        // Clear cached projects to force refresh with new search
        const groupData = this.getContactsDataForGroup(contactGroupName);
        groupData.cachedProjects = null;
        groupData.lastCacheKey = null;
        
        this.$scope.$applyAsync();
    }

    getProjectSearchTerm(contactGroupName) {
        return this.projectSearchTerms.get(contactGroupName) || '';
    }

    clearProjectSearch(contactGroupName) {
        this.projectSearchTerms.delete(contactGroupName);
        
        // Clear cached projects to force refresh
        const groupData = this.getContactsDataForGroup(contactGroupName);
        groupData.cachedProjects = null;
        groupData.lastCacheKey = null;
        
        this.$scope.$applyAsync();
    }

    highlightSearchMatch(text, searchTerm) {
        if (!searchTerm || !text) {
            return text;
        }
        
        const regex = new RegExp(`(${searchTerm})`, 'gi');
        const highlighted = text.replace(regex, '<mark>$1</mark>');
        return this.$sce.trustAsHtml(highlighted);
    }

    getProjectSearchPlaceholder(contactGroupName) {
        const projectCount = this.getProjectsForGroup(contactGroupName).length;
        const totalProjects = this.organizationProjects.length || 
                             this.getContactsDataForGroup(contactGroupName).projects.size;
        
        if (projectCount === 0) {
            return 'No projects to search';
        }
        if (projectCount === 1) {
            return 'Search 1 project';
        }
        return `Search ${totalProjects} projects`;
    }
} 